import {
  ajvResolver,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
  SelectItemProps,
  usePrevious,
  ValidatorsProps,
} from '@ui/src';
import { useCallback, useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import {
  useForm,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '@store';
import {
  AccountsCurrenciesFormValues,
  AccountsFlowsFormValues,
  ApplicationAgreement,
  ApplicationDocument,
  CRSAndFatcaFormValues,
  ExpectedFundsOriginsFormValues,
  FormValues,
  NewApplicationIndividualStepsStatuses,
  NewApplicationListItem,
  NewApplicationStatus,
  NewApplicationsValidatorResponse,
} from '../../types';
import {
  useLazyGetCustomersInfoDictionariesQuery,
  useLazyGetCustomersEmployeesListQuery,
} from '../../../../store/dictionaries/services';
import { updateApplicationStepStatuses } from '../helpers/update-application-step-status';
import {
  CustomerScoreUpdateStatus,
  OngoingCheckingStatus,
  OngoingMonitring,
} from '../../../common/types';
import { COFAndIndividualQuestionnarieFormValues } from '../../../individual/types';
import {
  useLazyGetApplicationAgreementsQuery,
  useLazyGetApplicationChatQuery,
  useLazyGetApplicationLastCalculatingScoreQuery,
  useLazyGetIndividualNewApplicationDocumentsQuery,
  useLazyGetNewApplicationInfoQuery,
  useLazyGetNewApplicationOrganizationQuery,
  useLazyGetNewApplicationsValidatorsQuery,
  useUpdateIndividualApplicationStatusMutation,
  useUpdateInividualQuestionnarieMutation,
  useUpdateNewApplicationMutation,
} from '../../store/services';
import getIndividualApplicationBody from '../helpers/get-individual-application-body';
import getCofandIndividualQuestionnarieBody from '../helpers/get-qestionnarie-body';
import { checkChatMessages, updateStepsStatus } from '../store/slices';

const useApplication = ({
  validators,
  clientsActivities,
  sourcesOfFunds,
  applicationStepsStatus,
  agreements,
  ongoingMonitoring,
  ongoingCheckingStatus,
  newApplicationInfo,
  questionnarieFields,
  accountsCurrenciesValues,
  expectedFundsOriginsValues,
  accountsFlowsValues,
  crsAndFatcaValues,
  haveSecondPassport,
  documents,
  applicationId,
  activePermissions,
  questionnarieGetValues,
  questionnarieSetError,
  onSaveAccountsCurrencies,
  onSaveAccountsFlows,
  onSaveExpectedFundsOrigins,
  onSaveCRSAndFatca,
  prevApplicationId,
  amlUpdateStatus,
  questionnarieSetValue,
  defaultQuestionnarieFormValues,
}: {
  validators?: NewApplicationsValidatorResponse;
  clientsActivities?: SelectItemProps[];
  sourcesOfFunds?: SelectItemProps[];
  applicationStepsStatus?: NewApplicationIndividualStepsStatuses;
  agreements?: ApplicationAgreement[];
  ongoingMonitoring?: OngoingMonitring[];
  ongoingCheckingStatus?: OngoingCheckingStatus;
  newApplicationInfo?: NewApplicationListItem;
  questionnarieFields?: COFAndIndividualQuestionnarieFormValues;
  accountsCurrenciesValues?: AccountsCurrenciesFormValues[];
  expectedFundsOriginsValues?: ExpectedFundsOriginsFormValues[];
  accountsFlowsValues?: AccountsFlowsFormValues[];
  crsAndFatcaValues?: CRSAndFatcaFormValues[];
  haveSecondPassport?: boolean;
  documents?: ApplicationDocument[];
  applicationId?: string;
  activePermissions?: PermissionEnum[];
  questionnarieGetValues: UseFormGetValues<COFAndIndividualQuestionnarieFormValues>;
  questionnarieSetError: UseFormSetError<COFAndIndividualQuestionnarieFormValues>;
  onSaveAccountsCurrencies: () => void;
  onSaveAccountsFlows: () => void;
  onSaveExpectedFundsOrigins: () => void;
  onSaveCRSAndFatca: () => void;
  prevApplicationId?: string;
  amlUpdateStatus?: CustomerScoreUpdateStatus;
  questionnarieSetValue: UseFormSetValue<COFAndIndividualQuestionnarieFormValues>;
  defaultQuestionnarieFormValues?: COFAndIndividualQuestionnarieFormValues;
}) => {
  const [defaultFormValues, setDefaultFormValues] = useState({});
  const [applicationLoadErrorVisible, setApplicationLoadErrorVisible] =
    useState(false);
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const [updateQuestionnarieError, setUpdateQuestionnarieError] = useState('');

  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [userCreated, setUserCreated] = useState(false);
  const [statusChangeError, setStatusChangeError] = useState('');

  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
    setError,
    clearErrors,
  } = useForm<FormValues>({
    resolver: ajvResolver(
      validators?.updateClientApplicationSchema as ValidatorsProps,
    ),
  });

  const fields = watch();
  const prevClientActivitiy = usePrevious(fields.clientActivityId);
  const prevSourceOfFunds = usePrevious(fields.sourceOfFundsIds);

  const employmentActivity = clientsActivities?.find(
    (item) => item.name === 'Employment',
  );
  const otherActivity = clientsActivities?.find(
    (item) => item.name === 'Other',
  );
  const businessOwnerActivity = clientsActivities?.find(
    (item) => item.name === 'Business Owner',
  );

  const otherSourceOfFunds = sourcesOfFunds?.find(
    (item) => item.name === 'Other',
  );

  const {
    riskScoreRanges,
    customersTransactionTypes,
    deliveryChannelsIndividual,
    productsIndividual,
    monthlyTurnover,
    numberOfIncomingTransactions,
    numberOfOutgoingTransactions,
    valueOfIncomingTransactions,
    valueOfOutgoingTransactions,
    maxValueOfIncomingPayment,
    maxValueOfOutgoingPayment,
  } = useAppSelector((state) => state.customersDictionaries);

  const stepsStatus = updateApplicationStepStatuses({
    values: fields,
    stepsStatus: applicationStepsStatus,
    sourcesOfFunds,
    clientsActivities,
    agreements,
    ongoingMonitoring,
    ongoingCheckingStatus,
    applicationScheme: newApplicationInfo?.applicationScheme,
    questionnarieFields,
    accountsCurrenciesValues,
    expectedFundsOriginsValues,
    accountsFlowsValues,
    crsAndFatcaValues,
    haveSecondPassport,
    documents,
  });

  const prevStepsStatus = usePrevious(stepsStatus);

  const [getDictionaries] = useLazyGetCustomersInfoDictionariesQuery();
  const [getApplicationChat] = useLazyGetApplicationChatQuery();
  const [getInfo, { isLoading, isFetching }] =
    useLazyGetNewApplicationInfoQuery();
  const [getApplicationOrganization] =
    useLazyGetNewApplicationOrganizationQuery();
  const [updateInfo, { isLoading: requestInProgress }] =
    useUpdateNewApplicationMutation();
  const [getValidators] = useLazyGetNewApplicationsValidatorsQuery();
  const [createNewIndividualCustomer, { isLoading: createInProgress }] =
    useUpdateIndividualApplicationStatusMutation();
  const [getApplicationLastCalculaingScore] =
    useLazyGetApplicationLastCalculatingScoreQuery();
  const [getIndividualApplicationDocuments] =
    useLazyGetIndividualNewApplicationDocumentsQuery();
  const [
    getApplicationAgreements,
    { isLoading: agreementsIsLoading, isFetching: agreementsIsFetching },
  ] = useLazyGetApplicationAgreementsQuery();
  const [getCustomersEmployeesList] = useLazyGetCustomersEmployeesListQuery();
  const [
    updateIndividualQuestionnarie,
    { isLoading: questionnarieUpdateInProgress },
  ] = useUpdateInividualQuestionnarieMutation();

  const getApplicationDocuments = (id: string) => {
    getIndividualApplicationDocuments(id);
  };

  const getInfoAction = useCallback(async () => {
    if (applicationId) {
      const response = (await getInfo(applicationId)) as {
        error: ResponseError;
        data: any;
      };

      getApplicationChat(applicationId);

      if (activePermissions?.includes(PermissionEnum.USERS_LIST)) {
        getCustomersEmployeesList();
      }

      if (response) {
        if (response?.error?.data?.message) {
          if (
            Array.isArray(response.error.data.message) &&
            response.error.data.message[0].property === 'coreError'
          ) {
            setApplicationLoadErrorVisible(true);
          }
        }
      }
    }
  }, [applicationId, activePermissions]);

  const onCreateCustomer = async () => {
    setStatusChangeError('');

    const response = (await createNewIndividualCustomer({
      id: applicationId as string,
      body: { status: NewApplicationStatus.ACCEPTED },
    })) as { error?: { data?: { message?: string } } };

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    } else {
      setUserCreated(true);

      setTimeout(() => {
        getApplicationOrganization(applicationId as string);
      }, 7000);
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setUpdateError('');
    setUpdateQuestionnarieError('');

    const body = getIndividualApplicationBody(
      values,
      newApplicationInfo?.basePersonalInfoEditable as boolean,
      newApplicationInfo?.applicationScheme,
    );

    const response = (await updateInfo({
      body: {
        ...body,
        participatesInPolitics: Boolean(
          values.participatesInPolitics === 'yes',
        ),
        isUSResident: Boolean(values.isUSResident === 'yes'),
      },
      id: applicationId as string,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError,
        setGlobalError: setUpdateError,
      });
    }

    if (
      (newApplicationInfo?.applicationScheme &&
        (newApplicationInfo?.applicationScheme?.civilActive ||
          newApplicationInfo?.applicationScheme?.criminalOfAnyActive ||
          newApplicationInfo?.applicationScheme
            ?.lastThreeYearsAddressHistoryActive ||
          newApplicationInfo?.applicationScheme
            ?.nationalInsuranceNumberOfDirectorsActive ||
          newApplicationInfo?.applicationScheme?.regulatoryActive ||
          newApplicationInfo?.applicationScheme
            ?.businessAndEmploymentActive)) ||
      newApplicationInfo?.applicationScheme?.id ===
        'ed560559-8354-4634-b6dd-eb341dfb551a'
    ) {
      const questionnarieValues = questionnarieGetValues();

      const questionnarieBody = getCofandIndividualQuestionnarieBody({
        questionnarieValues,
        scheme: newApplicationInfo?.applicationScheme,
      });

      const questionnarieResponse = (await updateIndividualQuestionnarie({
        id: newApplicationInfo?.id,
        body: questionnarieBody,
      })) as { error?: ResponseError };

      if (questionnarieResponse?.error) {
        responseErrorsResolver({
          errors: questionnarieResponse?.error,
          setError: questionnarieSetError,
          setGlobalError: setUpdateQuestionnarieError,
        });
      }

      if (
        newApplicationInfo?.applicationScheme?.id ===
        'ed560559-8354-4634-b6dd-eb341dfb551a'
      ) {
        onSaveAccountsCurrencies();
        onSaveAccountsFlows();
        onSaveExpectedFundsOrigins();
        onSaveCRSAndFatca();
      }
    }
  });

  useEffect(() => {
    if ((!newApplicationInfo && applicationId) || !prevApplicationId) {
      getInfoAction();
    }
  }, [applicationId, getInfoAction, newApplicationInfo, prevApplicationId]);

  useEffect(() => {
    if (applicationId) {
      if (
        !riskScoreRanges ||
        !customersTransactionTypes ||
        !deliveryChannelsIndividual ||
        !productsIndividual ||
        !sourcesOfFunds ||
        !clientsActivities ||
        !monthlyTurnover ||
        !numberOfIncomingTransactions ||
        !numberOfOutgoingTransactions ||
        !valueOfIncomingTransactions ||
        !valueOfOutgoingTransactions ||
        !maxValueOfIncomingPayment ||
        !maxValueOfOutgoingPayment
      ) {
        setTimeout(() => {
          getDictionaries();
        }, 10);
      }
    }
  }, [
    applicationId,
    riskScoreRanges,
    customersTransactionTypes,
    deliveryChannelsIndividual,
    productsIndividual,
    sourcesOfFunds,
    clientsActivities,
    monthlyTurnover,
    numberOfIncomingTransactions,
    numberOfOutgoingTransactions,
    valueOfIncomingTransactions,
    valueOfOutgoingTransactions,
    maxValueOfIncomingPayment,
    maxValueOfOutgoingPayment,
  ]);

  useEffect(() => {
    if (
      applicationId &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_LIST,
      )
    ) {
      getIndividualApplicationDocuments(applicationId);
    }
  }, [applicationId]);

  useEffect(() => {
    if (
      amlUpdateStatus &&
      amlUpdateStatus === CustomerScoreUpdateStatus.SUCCESS &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_SCORE_LIST,
      )
    ) {
      getApplicationLastCalculaingScore(applicationId as string);
    }
  }, [amlUpdateStatus, activePermissions]);

  useEffect(() => {
    if (!validators) {
      getValidators();
    }
  }, [getValidators, validators]);

  useEffect(() => {
    if (
      prevClientActivitiy &&
      prevClientActivitiy === employmentActivity?.value &&
      fields.clientActivityId !== employmentActivity?.value
    ) {
      setValue('nameOfEmployer', '');
      setValue('countryOfEmployerId', '');
      setValue('employeePosition', '');
      questionnarieSetValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy === businessOwnerActivity?.value &&
      fields.clientActivityId !== businessOwnerActivity?.value
    ) {
      setValue('countryOfEmployerId', '');
      questionnarieSetValue('nameOfCompany', '');
      questionnarieSetValue('ownership', '');
      questionnarieSetValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy ===
        clientsActivities?.find((item) => item.name === 'Self-employed')
          ?.value &&
      fields.clientActivityId !==
        clientsActivities?.find((item) => item.name === 'Self-employed')?.value
    ) {
      questionnarieSetValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy === otherActivity?.value &&
      fields.clientActivityId !== otherActivity?.value
    ) {
      setValue('typeOfActivityDescription', '');
    }
  }, [fields.clientActivityId, prevClientActivitiy]);

  useEffect(() => {
    if (
      prevSourceOfFunds &&
      prevSourceOfFunds.includes(otherSourceOfFunds?.value) &&
      !fields.sourceOfFundsIds?.includes(otherSourceOfFunds?.value as string)
    ) {
      setValue('sourceOfFundsDescription', '');
    }
  }, [fields.sourceOfFundsIds, prevSourceOfFunds]);

  useEffect(() => {
    if (newApplicationInfo) {
      const formValues = {
        firstName: newApplicationInfo.firstName || '',
        lastName: newApplicationInfo.lastName || '',
        phone: newApplicationInfo.phone || '',
        email: newApplicationInfo.email || '',
        monthlyTurnoverRangeId: newApplicationInfo.monthlyTurnoverRangeId || '',
        monthlyNumberIncomingTransactionsRangeId:
          newApplicationInfo.monthlyNumberIncomingTransactionsRangeId || '',
        monthlyNumberOutgoingTransactionsRangeId:
          newApplicationInfo.monthlyNumberOutgoingTransactionsRangeId || '',
        monthlyValueIncomingTransactionsRangeId:
          newApplicationInfo.monthlyValueIncomingTransactionsRangeId || '',
        monthlyValueOutgoingTransactionsRangeId:
          newApplicationInfo.monthlyValueOutgoingTransactionsRangeId || '',
        monthlyMaxValueIncomingPaymentRangeId:
          newApplicationInfo.monthlyMaxValueIncomingPaymentRangeId || '',
        monthlyMaxValueOutgoingPaymentRangeId:
          newApplicationInfo.monthlyMaxValueOutgoingPaymentRangeId || '',
        dob: newApplicationInfo.dob || '',
        countryId: newApplicationInfo?.country?.id || '',
        city: newApplicationInfo.city || '',
        postalCode: newApplicationInfo.postalCode || '',
        address1: newApplicationInfo.address1 || '',
        address2: newApplicationInfo.address2 || '',
        passportNumber: newApplicationInfo.passportNumber || '',
        passportExpiredDate: newApplicationInfo.passportExpiredDate || '',
        participatesInPolitics: !newApplicationInfo.participatesInPolitics
          ? 'no'
          : 'yes',
        isUSResident: !newApplicationInfo.isUSResident ? 'no' : 'yes',
        userId: newApplicationInfo.userId,
        sourceOfFundsIds:
          newApplicationInfo?.sourcesOfFunds?.edges.map(
            (item) => item.node.id,
          ) || [],
        sourceOfFundsDescription:
          newApplicationInfo.sourceOfFundsDescription || '',
        clientActivityId: newApplicationInfo?.clientActivityId || '',
        deliveryChannelId: newApplicationInfo?.deliveryChannelId || '',
        productIds:
          newApplicationInfo?.products?.edges.map((item) => item.node.id) || [],
        nationalityId: newApplicationInfo?.nationalityId || '',
        sumSubStatus: newApplicationInfo?.sumSubApplication?.reviewAnswer || '',
        typeOfActivityDescription:
          newApplicationInfo?.typeOfActivityDescription || '',
        nameOfEmployer: newApplicationInfo?.nameOfEmployer || '',
        employeePosition: newApplicationInfo?.employeePosition || '',
        countryOfEmployerId: newApplicationInfo?.countryOfEmployerId || '',
        taxNumber: newApplicationInfo?.taxNumber || '',
        birthCountryId: newApplicationInfo?.birthCountryId || '',
        placeOfBirth: newApplicationInfo?.placeOfBirth || '',
        correspondenceAddressSameAsRegisteredAddress:
          newApplicationInfo?.correspondenceAddressSameAsRegisteredAddress,
        correspondenceCountryId:
          newApplicationInfo?.correspondenceCountryId || '',
        correspondenceCity: newApplicationInfo?.correspondenceCity || '',
        correspondencePostalCode:
          newApplicationInfo?.correspondencePostalCode || '',
        correspondenceAddress1:
          newApplicationInfo?.correspondenceAddress1 || '',
        correspondenceAddress2:
          newApplicationInfo?.correspondenceAddress2 || '',
      };

      setDefaultFormValues(formValues);
      reset(formValues);

      getApplicationAgreements(newApplicationInfo.id);
    }
  }, [reset, newApplicationInfo]);

  useEffect(() => {
    setTimeout(() => {
      dispatch(checkChatMessages());
    }, 60000);
  }, [dispatch]);

  useEffect(() => {
    if (prevStepsStatus && !isEqual(stepsStatus, prevStepsStatus)) {
      dispatch(updateStepsStatus(stepsStatus));
    }
  }, [stepsStatus, prevStepsStatus, dispatch]);

  useEffect(() => {
    const handleBeforeUnload = (event: {
      preventDefault: () => void;
      returnValue: string;
    }) => {
      event.preventDefault();

      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };

    if (
      !isEqual(fields, defaultFormValues) ||
      !isEqual(questionnarieFields, defaultQuestionnarieFormValues)
    ) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [
    fields,
    defaultFormValues,
    questionnarieFields,
    defaultQuestionnarieFormValues,
  ]);

  useEffect(() => {
    let title = 'New Application';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  return {
    userCreated,
    isLoading,
    isFetching,
    applicationLoadErrorVisible,
    getApplicationDocuments,
    setStatusChangeError,
    setSuccessMessageVisible,
    onSubmit,
    requestInProgress,
    questionnarieUpdateInProgress,
    onCreateCustomer,
    createInProgress,
    fields,
    defaultFormValues,
    statusChangeError,
    setError,
    clearErrors,
    control,
    errors,
    setValue,
    isDirty,
    setDirtyPopupVisible,
    dirtyPopupVisible,
    successMessageVisible,
    updateError,
    updateQuestionnarieError,
    agreementsIsLoading,
    agreementsIsFetching,
  };
};

export default useApplication;
